import { Validators } from '@angular/forms';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { HandleRemainingBalanceCommand } from '@ordersnproducts/modules/order-builder/modules/specification/services/commands/handle-remaining-balance-validation.command';
import { SetProjectPropertiesCommand } from '@ordersnproducts/modules/order-builder/modules/specification/services/commands/set-project-properties.command';
import { ApplicationOptionLoader } from '@ordersnproducts/modules/order-builder/modules/specification/services/loaders/application-option-loader.service';
import { SpacingOptionLoader } from '@ordersnproducts/modules/order-builder/modules/specification/services/loaders/spacing-option-loader.service';
import { UNITS } from '@ordersnproducts/modules/shared/constants/units';
import { FormInfoMessageComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-info-message/form-info-message.component';
import { FormInputCheckboxComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-input-checkbox/form-input-checkbox.component';
import { FormInputNumberComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-input-number/form-input-number.component';
import {
  FieldConfig,
  NewLineField,
} from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-config.interface';
import { FieldInputNumberConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-input-number-config.interface';
import { FieldLabelConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-label-config.interface';
import { FormSelectComponent } from '../../../../../../../shared/modules/configurable-form/components/form-select/form-select.component';
import { FieldSelectConfig } from '../../../../../../../shared/modules/configurable-form/components/form-select/models/field-select-config.interface';
import { RmsContractProductsEffects } from '../../../../effects/contract-products.effects';
import { generateAddedValueFormName } from '../../../../helpers/form-added-value-name-generator';
import { AddedValueFieldConfig } from '../../../../models/form-added-value-field-config';
import { RmsFormConfig } from '../../../../models/rms-form-config';
import { AddedValueDisabler } from '../../../../providers/added-value-enabler.provider';
import { ContractProductsDisabler } from '../../../../providers/contract-product-enabler.provider';
import { PumpAvailability } from '../../../../providers/pump-availabe.provider';
import { RmsMaximumCapacityProvider } from '../../../../providers/rms-maximum-capacity.provider';
import { LoadContractBalance } from '../../../../services/config-loader/commands/load-contract-balance.command.service';
import { RmsHandleMaximumCapacity } from '../../../../services/config-loader/commands/rms-handle-max-capacity.command';
import { SetPaymentTermCommand } from '../../../../services/config-loader/commands/set-contract-payment.command.service';
import { AdddedValuesLoaderService } from '../../../../services/config-loader/loaders/added-value.loader.service';
import { PaymentTermsOptionLoaderService } from '../../../../services/config-loader/loaders/contract-payment-term-loader.service';
import { ContractProductsOptionLoaderService } from '../../../../services/config-loader/loaders/contract-products.loader.service';
import { ContractsOptionLoaderService } from '../../../../services/config-loader/loaders/contract.loader.service';
import { FormTextAreaComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-textarea/form-textarea.component';
import { CutOffEffects } from '../../../../effects/cut-off.effects';
import { FormInfoIconMessageComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-info-icon-message/form-info-icon-message.component';
import { DirectProjectProfilesOptionLoader } from '../../../../services/config-loader/loaders/direct-project-profile.service.loader';
import {
  LoadChildAddedValuesNotFormulating,
  LoadProductAddedValuesNotFormulating,
} from '../../../../services/config-loader/commands/load-added-value-not-formulating.command.service';
import { LoadCatalogs } from '../../../../services/config-loader/commands/catalogs.command.service';
import { SingleSlumpProductLegacyLoaderService } from '../../../../services/config-loader/loaders/single-catalogs.loader.service';
import { SingleCatalogsEffects } from '../../../../effects/single-catalogs.effects';
import { SetProjectProfileSlumpCommand } from '../../../../services/config-loader/commands/set-project-profile-slump.command.service';
import { LoadSlumpCatalog } from '../../../../services/config-loader/commands/load-slump-catalog.command';
import { ResetValueAddedCommand } from '../../../../services/config-loader/commands/reset-value-added.command.service';
import { FormInputComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-input/form-input.component';
import { SpecFieldTestIds } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-test-ids.enum';

export const rmsLegacy = {
  recipe: [
    {
      type: FormInputComponent,
      label: 'id',
      name: SpecFieldNames.ID,
      containerClassNames: ['d-none'],
      placeholder: 'id',
    } as FieldConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.product_description',
      name: SpecFieldNames.PRODUCT_SELECTION,
      dataTId: SpecFieldTestIds.PRODUCT_DESCRIPTION,
      containerClassNames: ['field-12x field-md-6x retail-control'],
      optionsLoader: ContractProductsOptionLoaderService,
      loading: ContractProductsDisabler.IsLoading,
      validation: [Validators.required],
      dynamicEffects: [RmsContractProductsEffects, CutOffEffects],
      placeholder: 'views.specifications.select_product',
      onChange: [LoadContractBalance, LoadCatalogs, LoadSlumpCatalog],
      rehydrate: true,
    } as FieldSelectConfig,
    {
      type: FormInputNumberComponent,
      label: 'views.specifications.quantity',
      containerClassNames: ['field-11x field-md-2x retail-control'],
      name: SpecFieldNames.QUANTITY,
      dataTId: SpecFieldTestIds.QUANTITY,
      min: 1,
      value: 1,
      step: 1,
      max: RmsMaximumCapacityProvider.RmsMaxCapacity,
      message: RmsMaximumCapacityProvider.MessageUsa,
      suffix: UNITS.CUBIC_YARD,
      validation: [Validators.required],
      decimals: 1,
      onChange: [HandleRemainingBalanceCommand],
    } as FieldInputNumberConfig,
    {
      type: FormInfoIconMessageComponent,
      name: SpecFieldNames.CONCRETE_MIN_QUANTITY_INFO,
      dataTId: SpecFieldTestIds.CONCRETE_MIN_QUANTITY_INFO,
      value: false,
      containerClassNames: ['field-1x field-md-1x retail-control'],
      text: 'views.specifications.special-message',
    } as FieldConfig,
    {
      type: FormInputCheckboxComponent,
      name: SpecFieldNames.ADJUSTMENT,
      dataTId: SpecFieldTestIds.ADJUSTMENT,
      value: false,
      containerClassNames: ['field-6x field-md-2x retail-control specification-adjustment'],
      placeholder: 'views.specifications.kicker',
    } as FieldConfig,
    {
      type: null,
      push: true,
    } as NewLineField,
    {
      type: FormInfoMessageComponent,
      name: SpecFieldNames.PRODUCT_PUMP_INFORMATIVE,
      dataTId: SpecFieldTestIds.PRODUCT_PUMP_INFORMATIVE,
      containerClassNames: ['field-12x field-md-6x'],
      text: 'views.specifications.pumping_not_available',
      hidden: PumpAvailability.ShouldHide,
    } as FieldLabelConfig,
    {
      type: null,
      push: true,
    } as NewLineField,
    {
      type: FormSelectComponent,
      label: 'views.specifications.contract',
      name: SpecFieldNames.CONTRACT_SELECT,
      dataTId: SpecFieldTestIds.CONTRACT_SELECT,
      containerClassNames: ['field-12x field-md-6x retail-control'],
      validation: [Validators.required],
      optionsLoader: ContractsOptionLoaderService,
      placeholder: 'views.specifications.select_contract',
      onChange: [
        SetPaymentTermCommand,
        RmsHandleMaximumCapacity,
        LoadProductAddedValuesNotFormulating,
        ResetValueAddedCommand,
      ],
      rehydrate: true,
    } as FieldSelectConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.paymentTerms',
      name: SpecFieldNames.PAYMENT_TERMS,
      dataTId: SpecFieldTestIds.PAYMENT_TERMS,
      containerClassNames: ['field-12x field-md-2x retail-control'],
      disabled: true,
      optionsLoader: PaymentTermsOptionLoaderService,
    } as FieldSelectConfig,
    {
      type: null,
      push: true,
    } as NewLineField,
    {
      type: FormSelectComponent,
      label: 'views.specifications.valueAdded',
      name: generateAddedValueFormName(0),
      dataTId: SpecFieldTestIds.VALUE_ADDED_FIRST,
      index: 0,
      loading: AddedValueDisabler.IsLoading,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      optionsLoader: AdddedValuesLoaderService,
      onChange: [LoadChildAddedValuesNotFormulating],
      placeholder: 'views.specifications.select_valueAdded',
      rehydrate: true,
    } as AddedValueFieldConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.valueAdded',
      name: generateAddedValueFormName(1),
      dataTId: SpecFieldTestIds.VALUE_ADDED_SECOND,
      index: 1,
      loading: AddedValueDisabler.IsLoading,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      onChange: [LoadChildAddedValuesNotFormulating],
      optionsLoader: AdddedValuesLoaderService,
      placeholder: 'views.specifications.select_valueAdded',
      rehydrate: true,
    } as AddedValueFieldConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.valueAdded',
      name: generateAddedValueFormName(2),
      dataTId: SpecFieldTestIds.VALUE_ADDED_THIRD,
      index: 2,
      loading: AddedValueDisabler.IsLoading,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      onChange: [LoadChildAddedValuesNotFormulating],
      optionsLoader: AdddedValuesLoaderService,
      placeholder: 'views.specifications.select_valueAdded',
      rehydrate: true,
    } as AddedValueFieldConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.valueAdded',
      name: generateAddedValueFormName(3),
      dataTId: SpecFieldTestIds.VALUE_ADDED_FOURTH,
      index: 3,
      loading: AddedValueDisabler.IsLoading,
      limit: true,
      optionsLoader: AdddedValuesLoaderService,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_valueAdded',
      rehydrate: true,
    } as AddedValueFieldConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.project_profiles',
      name: SpecFieldNames.PROJECT_PROFILES,
      dataTId: SpecFieldTestIds.PROJECT_PROFILES,
      containerClassNames: ['field-12x field-md-6x retail-control'],
      placeholder: 'views.specifications.select_projectProfiles',
      optionsLoader: DirectProjectProfilesOptionLoader,
      onChange: [SetProjectPropertiesCommand, SetProjectProfileSlumpCommand],
    } as FieldSelectConfig,
    {
      type: null,
      push: true,
    } as NewLineField,
    {
      type: FormSelectComponent,
      label: 'views.specifications.unload_type',
      name: SpecFieldNames.UNLOAD_TYPE,
      dataTId: SpecFieldTestIds.UNLOAD_TYPE,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'Direct Flow',
      validation: [Validators.required],
      disabled: true,
    } as FieldSelectConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.application',
      name: SpecFieldNames.APPLICATION,
      dataTId: SpecFieldTestIds.APPLICATION,
      optionsLoader: ApplicationOptionLoader,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_application',
      validation: [Validators.required],
    } as FieldSelectConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.spacing',
      name: SpecFieldNames.SPACING,
      dataTId: SpecFieldTestIds.SPACING,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_spacing',
      optionsLoader: SpacingOptionLoader,
      validation: [Validators.required],
    } as FieldSelectConfig,
    {
      type: FormSelectComponent,
      label: 'views.specifications.slump',
      name: SpecFieldNames.SLUMP_PRODUCT,
      dataTId: SpecFieldTestIds.SLUMP,
      containerClassNames: ['field-12x field-md-3x retail-control'],
      placeholder: 'views.specifications.select_slump',
      optionsLoader: SingleSlumpProductLegacyLoaderService,
      validation: [Validators.required],
      dynamicEffects: [SingleCatalogsEffects],
      rehydrate: true,
    } as FieldSelectConfig,
    {
      type: null,
      push: true,
    } as NewLineField,
    {
      type: FormTextAreaComponent,
      label: 'views.specifications.additional_info',
      name: SpecFieldNames.ADDITIONAL_INFO,
      dataTId: SpecFieldTestIds.ADDITIONAL_INFO,
      containerClassNames: ['field-12x field-md-12x retail-control'],
      placeholder: 'views.specifications.enter_comment',
      maxlength: '63',
      validation: [],
    } as FieldConfig,
    {
      type: null,
      push: true,
    } as NewLineField,
  ],
} as RmsFormConfig;
